import { formatPlainDate } from "@sa-nu/shared-utils/date";
import { formatCurrency, toUnitCharacter } from "@sa-nu/shared-utils/price";

import type { GroupedFeeItem } from "../../utils/group-fee-item-by-night";

export type PriceBreakdownMessages = {
  dynamicPricingLabel: string;
  additionalGuestFeeLabel: string;
  total: string;
  includingTax: string;
  unitCharacter?: string;
  translateLabelByKey?: (key: string, fallbackLabel: string) => string;
};

const JA_MESSAGES: PriceBreakdownMessages = {
  dynamicPricingLabel: "お部屋・日程による変動料金",
  additionalGuestFeeLabel: "人数追加料金を含む",
  total: "合計",
  includingTax: "(税込)",
};

type Props = {
  groupedFeeItems: GroupedFeeItem[];
  totalAmount: number;
  messages?: PriceBreakdownMessages;
};

export function PriceBreakdown({
  groupedFeeItems,
  totalAmount,
  messages = JA_MESSAGES,
}: Props) {
  const uc = toUnitCharacter(messages.unitCharacter ?? "¥");
  return (
    <div className="text-base">
      {groupedFeeItems.map((item) => {
        const dynamicLabels = buildDynamicLabels(item, messages);
        return (
          <div
            key={`${item.label} - ${item.night?.toString()}`}
            className="flex min-h-[60px] items-center justify-between border-t border-[rgba(37,39,42,0.1)] py-[10px]"
          >
            <div
              className={`flex items-center gap-[10px] ${
                item.night ? "" : "font-medium"
              }`}
            >
              {item.night && (
                <span>
                  {formatPlainDate(item.night, { withDayOfWeek: true })}
                </span>
              )}
              {dynamicLabels.length > 0 ? (
                <span className="flex flex-col text-[10px] tracking-[0.15px] md:text-[14px]">
                  {dynamicLabels.map((label) => (
                    <span key={label}>{label}</span>
                  ))}
                </span>
              ) : (
                <span>
                  {item.key && messages.translateLabelByKey
                    ? messages.translateLabelByKey(item.key, item.label)
                    : item.label}
                </span>
              )}
            </div>
            <div className="font-light">
              {formatCurrency(item.amount, { unitCharacter: uc })}
            </div>
          </div>
        );
      })}
      <div className="flex h-[60px] items-center justify-between border-t border-[rgba(37,39,42,1.0)]">
        <div className="font-medium">{messages.total}</div>
        <div className="text-xl font-light">
          {formatCurrency(totalAmount, { unitCharacter: uc })}
          <span className="text-xs">{messages.includingTax}</span>
        </div>
      </div>
    </div>
  );
}

function buildDynamicLabels(
  item: GroupedFeeItem,
  messages: PriceBreakdownMessages,
): string[] {
  const labels: string[] = [];
  if (item.hasDynamicPricing) labels.push(messages.dynamicPricingLabel);
  if (item.hasAdditionalGuestFee) labels.push(messages.additionalGuestFeeLabel);
  return labels;
}
